/* eslint-disable @opendoor/no-html-elements -- root loader renders before BricksProvider */
import type { ReactNode } from 'react';
import { startTransition, useCallback, useEffect, useRef, useState } from 'react';

import { globalTrackEvent, useLoadObservability } from 'platform/observability';
import { useHydrated } from 'platform/router/utils/useHydrated';
import { useLocation } from 'react-router';
import { IS_PROD } from 'shared/helpers/env';
import { MAPBOX_ACCESS_TOKEN } from 'shared/helpers/globals';
import { useIsomorphicLayoutEffect } from 'shared/hooks/useIsomorphicLayoutEffect';
import { normalizePathname } from 'shared/hooks/usePath';

import {
  getRouteContentCommitKey,
  LOADER_STUCK_WARNING_MS,
  useLoaderContext,
} from './LoaderContext';
import { OpendoorLogo } from './OpendoorLogo';
import type { RootLoaderAddressPreviewDetail } from './rootLoaderAddressPreviewUtils';
import {
  extractRootLoaderAddressUuid,
  ROOT_LOADER_ADDRESS_PREVIEW_EVENT,
} from './rootLoaderAddressPreviewUtils';

export const ROOT_LOADER_APP_CONTENTS_ID = 'cake-app-contents';
export const ROOT_LOADER_ID = 'root-loader';
export const ROOT_LOADER_DONE_EVENT = 'cake:root-loader-done';
export const ROOT_LOADER_VISIBLE_EVENT = 'cake:root-loader-visible';

const rootLoaderStages = [
  'Pulling local market data',
  'Reviewing recent home sales',
  'Checking trends in your area',
  'Bringing it all together',
  'Almost ready',
];

const rootLoaderColors = {
  brand: 'var(--color-contentAccentBrand, #0041E7)',
  content: 'var(--color-contentPrimary, #23201d)',
  inactiveContent: 'var(--color-contentStateInactivePrimary, rgba(33, 32, 31, 0.35))',
  inactiveBrand: 'var(--color-contentStateInactiveAccentBrand, rgba(0, 65, 231, 0.35))',
  surface: 'var(--color-backgroundPrimary, #ffffff)',
  mutedSurface: 'var(--color-backgroundSecondary, #f9f8f8)',
  mapPlaceholder: 'var(--color-backgroundTertiary, #e8e6e4)',
} as const;

const rootLoaderEase = 'cubic-bezier(0.65, 0, 0.35, 1)';
const rootLoaderAddressEase = 'cubic-bezier(0.22, 1, 0.36, 1)';
const rootLoaderFontFamily = '"Graphik", system-ui, -apple-system, "Segoe UI", sans-serif';
const rootLoaderContentWidth = 320;
const rootLoaderContentVisualInset = 24;
const rootLoaderAddressRevealDelay = 280;
const rootLoaderAddressContentRevealDelay = 260;
const rootLoaderMetricsVersion = 'handoff_loader_v1';
const rootLoaderSkeletonMask =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='310' height='87' viewBox='0 0 310 87'%3E%3Crect x='0' y='0' width='114' height='87' rx='8' fill='white'/%3E%3Crect x='130' y='22' width='180' height='16' rx='8' fill='white'/%3E%3Crect x='130' y='50' width='140' height='16' rx='8' fill='white'/%3E%3C/svg%3E\")";

type RootLoaderPhase = 'running' | 'fading' | 'gone';

type RootLoaderAddressPreviewStatus = 'not_applicable' | 'pending' | 'preview' | 'unavailable';

type RootLoaderBlocker =
  | 'address_reveal'
  | 'hydration'
  | 'hydration_effects'
  | 'loader_entries'
  | 'route_content_commit';

const rootLoaderBlockerNames: ReadonlySet<string> = new Set<RootLoaderBlocker>([
  'address_reveal',
  'hydration',
  'hydration_effects',
  'loader_entries',
  'route_content_commit',
]);

type RootLoaderCoords = { lat: number; lng: number };

type RootLoaderAddressLines = {
  localityLine: string;
  streetLine: string;
};

type RootLoaderMetricAction = 'abandoned' | 'address_preview' | 'hidden' | 'map_loaded' | 'shown';

type RootLoaderMetricPayload = Record<string, boolean | number | string | undefined>;

function getRootLoaderMetricLabel(action: RootLoaderMetricAction): string {
  return `root-loader-${action.replaceAll('_', '-')}`;
}

function getRootLoaderMetricPath(pathname: string): string {
  return normalizePathname(pathname) || 'root';
}

function getRootLoaderMetricSurface(pathname: string): string {
  const [surface = 'root'] = getRootLoaderMetricPath(pathname).split('/');
  return surface || 'root';
}

function getRootLoaderDurationMs(startedAt: number | null): number | undefined {
  if (startedAt == null) {
    return undefined;
  }
  return Math.round(performance.now() - startedAt);
}

function getRootLoaderMetricList(values: Iterable<string>): string {
  const serializedValues = Array.from(values).filter(Boolean).sort().join(',');
  return serializedValues || 'none';
}

function getRootLoaderBlockersFromMetricList(value: string): RootLoaderBlocker[] {
  if (value === 'none') {
    return [];
  }
  return value
    .split(',')
    .filter((blocker): blocker is RootLoaderBlocker => rootLoaderBlockerNames.has(blocker));
}

function getRootLoaderBlockers({
  entriesSize,
  hasCurrentRouteContentCommitted,
  hydrated,
  hydrationEffectsSettled,
  isAddressRevealPending,
}: {
  entriesSize: number;
  hasCurrentRouteContentCommitted: boolean;
  hydrated: boolean;
  hydrationEffectsSettled: boolean;
  isAddressRevealPending: boolean;
}): RootLoaderBlocker[] {
  const blockers: RootLoaderBlocker[] = [];
  if (!hydrated) blockers.push('hydration');
  if (!hydrationEffectsSettled) blockers.push('hydration_effects');
  if (!hasCurrentRouteContentCommitted) blockers.push('route_content_commit');
  if (entriesSize > 0) blockers.push('loader_entries');
  if (isAddressRevealPending) blockers.push('address_reveal');
  return blockers;
}

function trackRootLoaderAction(
  action: RootLoaderMetricAction,
  payload: RootLoaderMetricPayload,
): void {
  globalTrackEvent(
    'loading-duration',
    getRootLoaderMetricLabel(action),
    {
      data: {
        loader_event: action,
        loader_name: 'root_loader',
        loader_version: rootLoaderMetricsVersion,
        ...payload,
      },
    },
    action === 'hidden' || action === 'abandoned' ? { addTiming: true } : undefined,
  );
}

function getRootLoaderCoords(
  preview: RootLoaderAddressPreviewDetail | null,
): RootLoaderCoords | null {
  return preview?.lat != null &&
    preview.lng != null &&
    Number.isFinite(preview.lat) &&
    Number.isFinite(preview.lng)
    ? { lat: preview.lat, lng: preview.lng }
    : null;
}

function getRootLoaderAddressLines(address: string | null): RootLoaderAddressLines {
  if (!address) {
    return { localityLine: '', streetLine: '' };
  }

  const [streetLine = '', ...localityParts] = address.split(/,\s*/);
  return {
    localityLine: localityParts.join(', '),
    streetLine,
  };
}

function getRootLoaderMapUrl(coords: RootLoaderCoords | null): string | null {
  if (!coords) {
    return null;
  }

  const mapImagePath = `https://api.mapbox.com/styles/v1/opendoor/cktkqasmc1gbe17pfo2cm7ybb/static/${coords.lng},${coords.lat},15,0/228x174@2x`;
  const params = new URLSearchParams({ access_token: MAPBOX_ACCESS_TOKEN });
  return `${mapImagePath}?${params}`;
}

function getNextRootLoaderStage(currentStep: number): number {
  return Math.min(currentStep + 1, rootLoaderStages.length - 1);
}

function isRootLoaderAddressPreviewForAddress(
  preview: RootLoaderAddressPreviewDetail | null,
  addressUUID: string | null,
): boolean {
  if (!preview) {
    return true;
  }
  if (!addressUUID) {
    return !preview.addressUUID;
  }
  return preview.addressUUID === addressUUID;
}

function getRootLoaderAddressPreviewStatus(
  preview: RootLoaderAddressPreviewDetail | null,
  addressUUID: string | null,
): RootLoaderAddressPreviewStatus {
  if (preview) {
    return 'preview';
  }
  if (addressUUID) {
    return 'unavailable';
  }
  return 'not_applicable';
}

function isCustomEvent<T>(event: Event): event is CustomEvent<T> {
  return 'detail' in event;
}

function useRootLoaderAddressPreview(addressUUID: string | null): {
  addressPreview: RootLoaderAddressPreviewDetail | null;
  addressPreviewStatus: RootLoaderAddressPreviewStatus;
  mapLoaded: boolean;
  setMapLoaded: (loaded: boolean) => void;
} {
  const [addressPreview, setAddressPreview] = useState<RootLoaderAddressPreviewDetail | null>(null);
  const [addressPreviewStatus, setAddressPreviewStatus] = useState<RootLoaderAddressPreviewStatus>(
    addressUUID ? 'pending' : 'not_applicable',
  );
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    startTransition(() => {
      setAddressPreview(null);
      setAddressPreviewStatus(addressUUID ? 'pending' : 'not_applicable');
      setMapLoaded(false);
    });
  }, [addressUUID]);

  useEffect(() => {
    const handleAddressPreview = (event: Event) => {
      if (!isCustomEvent<RootLoaderAddressPreviewDetail | null>(event)) return;

      const preview = event.detail;
      if (preview && !isRootLoaderAddressPreviewForAddress(preview, addressUUID)) return;

      startTransition(() => {
        setAddressPreview(preview);
        setAddressPreviewStatus(getRootLoaderAddressPreviewStatus(preview, addressUUID));
        setMapLoaded(false);
      });
    };

    window.addEventListener(ROOT_LOADER_ADDRESS_PREVIEW_EVENT, handleAddressPreview);
    return () => {
      window.removeEventListener(ROOT_LOADER_ADDRESS_PREVIEW_EVENT, handleAddressPreview);
    };
  }, [addressUUID]);

  return { addressPreview, addressPreviewStatus, mapLoaded, setMapLoaded };
}

function useRootLoaderAddressReveal(
  addressUUID: string | null,
  hasAddress: boolean,
): {
  isAddressContentRevealed: boolean;
} {
  const [revealedAddressContentUUID, setRevealedAddressContentUUID] = useState<string | null>(null);

  useEffect(() => {
    if (!addressUUID || !hasAddress) {
      setRevealedAddressContentUUID(null);
      return undefined;
    }

    if (revealedAddressContentUUID === addressUUID) {
      return undefined;
    }

    const contentTimeoutId = window.setTimeout(() => {
      startTransition(() => setRevealedAddressContentUUID(addressUUID));
    }, rootLoaderAddressRevealDelay + rootLoaderAddressContentRevealDelay);
    return () => {
      window.clearTimeout(contentTimeoutId);
    };
  }, [addressUUID, hasAddress, revealedAddressContentUUID]);

  return {
    isAddressContentRevealed: Boolean(addressUUID && revealedAddressContentUUID === addressUUID),
  };
}

function RootLoaderAddressRow({
  ariaHidden,
  children,
  id,
  reserveChecklistGap = false,
}: {
  ariaHidden?: boolean;
  children: ReactNode;
  id?: string;
  reserveChecklistGap?: boolean;
}) {
  return (
    <div
      id={id}
      aria-hidden={ariaHidden}
      style={{
        height: 87,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        marginBottom: reserveChecklistGap ? 32 : 0,
        position: 'relative',
      }}
    >
      {children}
    </div>
  );
}

function RootLoaderAddressMedia({ children }: { children?: ReactNode }) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'relative',
        width: 114,
        height: 87,
        borderRadius: 8,
        background: rootLoaderColors.mapPlaceholder,
        boxShadow: '0 0 0 1px rgba(121, 118, 117, 0.26)',
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      {children}
    </div>
  );
}

function RootLoaderMapPreview({
  coords,
  mapLoaded,
  mapUrl,
  onMapLoad,
}: {
  coords: RootLoaderCoords | null;
  mapLoaded: boolean;
  mapUrl: string | null;
  onMapLoad: () => void;
}) {
  return (
    <RootLoaderAddressMedia>
      {mapUrl && (
        <img
          src={mapUrl}
          alt=""
          onLoad={onMapLoad}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: mapLoaded ? 1 : 0,
            transition: 'opacity 220ms ease-out',
          }}
        />
      )}
      {coords && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: rootLoaderColors.brand,
            border: `2px solid ${rootLoaderColors.surface}`,
            boxShadow: '0 0 0 1px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.2)',
            opacity: mapLoaded ? 1 : 0,
            transition: 'opacity 220ms ease-out',
          }}
        />
      )}
    </RootLoaderAddressMedia>
  );
}

function RootLoaderPropertyPreview({
  localityLine,
  streetLine,
  coords,
  mapLoaded,
  mapUrl,
  onMapLoad,
}: {
  localityLine: string;
  streetLine: string;
  coords: RootLoaderCoords | null;
  mapLoaded: boolean;
  mapUrl: string | null;
  onMapLoad: () => void;
}) {
  return (
    <div
      id="root-loader-property-preview"
      style={{
        height: 119,
      }}
    >
      <RootLoaderAddressRow>
        <RootLoaderMapPreview
          coords={coords}
          mapLoaded={mapLoaded}
          mapUrl={mapUrl}
          onMapLoad={onMapLoad}
        />
        <p
          id="root-loader-address"
          aria-label={[streetLine, localityLine].filter(Boolean).join(', ')}
          style={{
            minWidth: 0,
            flex: '1 1 auto',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: rootLoaderFontFamily,
            fontWeight: 500,
            fontSize: 16,
            lineHeight: 1.25,
            letterSpacing: 0,
            color: rootLoaderColors.content,
          }}
        >
          <span
            style={{
              display: 'block',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {streetLine}
          </span>
          {localityLine ? (
            <span
              style={{
                display: 'block',
                maxWidth: '100%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {localityLine}
            </span>
          ) : null}
        </p>
      </RootLoaderAddressRow>
    </div>
  );
}

function RootLoaderAddressSkeleton() {
  return (
    <RootLoaderAddressRow id="root-loader-address-skeleton" ariaHidden reserveChecklistGap>
      <RootLoaderAddressMedia />
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <span
          className="root-loader-skeleton-piece"
          style={{
            display: 'block',
            width: 180,
            height: 16,
            borderRadius: 8,
          }}
        />
        <span
          className="root-loader-skeleton-piece"
          style={{
            display: 'block',
            width: 140,
            height: 16,
            borderRadius: 8,
          }}
        />
      </div>
    </RootLoaderAddressRow>
  );
}

function RootLoaderChecklist({
  dotPulseDelay,
  fillTransition,
  step,
}: {
  dotPulseDelay: number;
  fillTransition: string;
  step: number;
}) {
  const visibleRows = 3;
  const rowHeight = 39;
  const dotSize = 10;
  const lineWidth = 2;
  const listLength = rootLoaderStages.length * visibleRows;
  const viewportHeight = rowHeight * visibleRows;
  const rowCenter = rowHeight / 2;
  const lineOffset = dotSize / 2 - lineWidth / 2;

  return (
    <div
      aria-hidden="true"
      style={{ position: 'relative', alignSelf: 'stretch', height: viewportHeight }}
    >
      <span
        key={step}
        aria-hidden="true"
        style={{
          pointerEvents: 'none',
          position: 'absolute',
          top: rowHeight + (rowHeight - dotSize) / 2,
          left: 0,
          width: dotSize,
          height: dotSize,
          borderRadius: '50%',
          background: 'transparent',
          animation: `cake-root-loader-dot-pulse 3000ms ease-in-out ${dotPulseDelay}ms both`,
        }}
      />

      <div
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          WebkitMaskImage:
            'linear-gradient(to bottom, transparent 0%, black 22%, black 78%, transparent 100%)',
          maskImage:
            'linear-gradient(to bottom, transparent 0%, black 22%, black 78%, transparent 100%)',
        }}
      >
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: rowHeight,
            transform: `translateY(${-step * rowHeight}px)`,
            transition: `transform 700ms ${rootLoaderEase}`,
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: lineOffset,
              top: rowCenter,
              width: lineWidth,
              height: (listLength - 1) * rowHeight,
              background: 'rgba(0, 65, 231, 0.18)',
              borderRadius: 1,
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: lineOffset,
              top: rowCenter,
              width: lineWidth,
              height: step * rowHeight,
              background: rootLoaderColors.brand,
              borderRadius: 1,
              transition: `height 700ms ${rootLoaderEase}`,
            }}
          />

          <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {Array.from({ length: listLength }, (_, i) => {
              const label = rootLoaderStages[i % rootLoaderStages.length];
              const isActive = i === step;
              const isPast = i < step;
              return (
                <li
                  key={i}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: i * rowHeight,
                    height: rowHeight,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <span
                    style={{
                      position: 'relative',
                      display: 'inline-flex',
                      flexShrink: 0,
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: dotSize,
                      height: dotSize,
                    }}
                  >
                    <span
                      style={{
                        width: dotSize,
                        height: dotSize,
                        borderRadius: '50%',
                        boxSizing: 'border-box',
                        border: `1.5px solid ${
                          isActive || isPast
                            ? 'rgba(255, 255, 255, 0.35)'
                            : rootLoaderColors.inactiveBrand
                        }`,
                        background:
                          isActive || isPast
                            ? rootLoaderColors.brand
                            : rootLoaderColors.mutedSurface,
                        transition: `background-color ${fillTransition}, border-color ${fillTransition}`,
                        display: 'block',
                      }}
                    />
                  </span>
                  <span
                    style={{
                      marginLeft: dotSize,
                      whiteSpace: 'nowrap',
                      fontFamily: rootLoaderFontFamily,
                      fontWeight: 400,
                      fontSize: 16,
                      lineHeight: 1.25,
                      letterSpacing: 0,
                      color: isActive ? rootLoaderColors.content : rootLoaderColors.inactiveContent,
                      transition: `color ${fillTransition}`,
                    }}
                  >
                    {label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

export function RootLoader() {
  useLoadObservability();

  const hydrated = useHydrated();
  const [step, setStep] = useState(0);
  const [hydrationEffectsSettled, setHydrationEffectsSettled] = useState(false);
  const [phase, setPhase] = useState<RootLoaderPhase>('running');
  const hasTrackedInitialCycleRef = useRef(false);
  const hiddenTrackedRef = useRef(false);
  const sawAddressPreviewRef = useRef(false);
  const sawAddressUnavailableRef = useRef(false);
  const sawMapLoadedRef = useRef(false);
  const sawBlockersRef = useRef<Set<RootLoaderBlocker>>(new Set());
  const shownAtRef = useRef<number | null>(null);
  const { entries, routeContentCommitKey } = useLoaderContext();
  const location = useLocation();
  const currentRouteContentCommitKey = getRouteContentCommitKey(location);
  const hasCurrentRouteContentCommitted = routeContentCommitKey === currentRouteContentCommitKey;
  const addressUUID = extractRootLoaderAddressUuid(location.pathname);
  const { addressPreview, addressPreviewStatus, mapLoaded, setMapLoaded } =
    useRootLoaderAddressPreview(addressUUID);
  const { localityLine, streetLine } = getRootLoaderAddressLines(addressPreview?.address ?? null);
  const hasAddress = streetLine.length > 0;
  const { isAddressContentRevealed } = useRootLoaderAddressReveal(addressUUID, hasAddress);
  const isAddressRevealPending = Boolean(addressUUID && hasAddress && !isAddressContentRevealed);
  const isLoading =
    !hydrated ||
    !hydrationEffectsSettled ||
    !hasCurrentRouteContentCommitted ||
    entries.size > 0 ||
    isAddressRevealPending;
  const isWaitingForRouteContentCommit =
    hydrated && hydrationEffectsSettled && !hasCurrentRouteContentCommitted && entries.size === 0;
  const blockers = getRootLoaderBlockers({
    entriesSize: entries.size,
    hasCurrentRouteContentCommitted,
    hydrated,
    hydrationEffectsSettled,
    isAddressRevealPending,
  });
  const blockersKey = getRootLoaderMetricList(blockers);
  const loaderKeys = getRootLoaderMetricList(entries.keys());
  const routePath = getRootLoaderMetricPath(location.pathname);
  const routeSurface = getRootLoaderMetricSurface(location.pathname);
  const coords = getRootLoaderCoords(addressPreview);
  const mapUrl = getRootLoaderMapUrl(coords);
  const finishFade = useCallback(() => {
    startTransition(() => {
      setPhase((current) => (current === 'fading' ? 'gone' : current));
    });
  }, []);
  const buildRootLoaderMetricPayload = useCallback(
    (payload?: RootLoaderMetricPayload): RootLoaderMetricPayload => ({
      active_loader_count: entries.size,
      active_loader_keys: loaderKeys,
      address_state: addressPreviewStatus,
      blockers: blockersKey,
      duration_ms: getRootLoaderDurationMs(shownAtRef.current),
      has_address_uuid: Boolean(addressUUID),
      route_path: routePath,
      route_surface: routeSurface,
      ...payload,
    }),
    [
      addressPreviewStatus,
      addressUUID,
      blockersKey,
      entries.size,
      loaderKeys,
      routePath,
      routeSurface,
    ],
  );

  useIsomorphicLayoutEffect(() => {
    if (hydrated) {
      setHydrationEffectsSettled(true);
    }
  }, [hydrated]);

  useIsomorphicLayoutEffect(() => {
    startTransition(() => {
      if (isLoading) {
        if (phase === 'gone') {
          setStep(0);
        }
        setPhase('running');
      } else {
        setPhase((current) => (current === 'running' ? 'fading' : current));
      }
    });
  }, [isLoading, phase]);

  useEffect(() => {
    if (!isWaitingForRouteContentCommit) {
      return undefined;
    }

    const startedAt = performance.now();
    const timeout = window.setTimeout(() => {
      emitRouteCommitStuck({
        entriesCount: entries.size,
        currentRouteContentCommitKey,
        durationMs: performance.now() - startedAt,
        hasCurrentRouteContentCommitted,
        hydrated,
        hydrationEffectsSettled,
        routeContentCommitKey,
      });
    }, LOADER_STUCK_WARNING_MS);

    return () => window.clearTimeout(timeout);
  }, [
    currentRouteContentCommitKey,
    entries.size,
    hasCurrentRouteContentCommitted,
    hydrated,
    hydrationEffectsSettled,
    isWaitingForRouteContentCommit,
    routeContentCommitKey,
  ]);

  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent(phase === 'gone' ? ROOT_LOADER_DONE_EVENT : ROOT_LOADER_VISIBLE_EVENT),
    );
  }, [phase]);

  useEffect(() => {
    for (const blocker of getRootLoaderBlockersFromMetricList(blockersKey)) {
      sawBlockersRef.current.add(blocker);
    }
  }, [blockersKey]);

  useEffect(() => {
    if (phase !== 'running' || typeof window === 'undefined' || shownAtRef.current != null) {
      return;
    }

    const startedAt = hasTrackedInitialCycleRef.current ? performance.now() : 0;
    hasTrackedInitialCycleRef.current = true;
    hiddenTrackedRef.current = false;
    sawAddressPreviewRef.current = false;
    sawAddressUnavailableRef.current = false;
    sawMapLoadedRef.current = false;
    sawBlockersRef.current = new Set(getRootLoaderBlockersFromMetricList(blockersKey));
    shownAtRef.current = startedAt;

    trackRootLoaderAction(
      'shown',
      buildRootLoaderMetricPayload({
        duration_ms: undefined,
        elapsed_since_navigation_start_ms: Math.round(performance.now()),
        started_from_navigation_start: startedAt === 0,
      }),
    );
  }, [blockersKey, buildRootLoaderMetricPayload, phase]);

  useEffect(() => {
    if (phase !== 'running' || shownAtRef.current == null) {
      return;
    }

    if (addressPreviewStatus === 'preview' && !sawAddressPreviewRef.current) {
      sawAddressPreviewRef.current = true;
      trackRootLoaderAction(
        'address_preview',
        buildRootLoaderMetricPayload({
          has_map_coords: Boolean(coords),
          has_map_url: Boolean(mapUrl),
        }),
      );
    }

    if (addressPreviewStatus === 'unavailable' && !sawAddressUnavailableRef.current) {
      sawAddressUnavailableRef.current = true;
      trackRootLoaderAction('address_preview', buildRootLoaderMetricPayload());
    }
  }, [addressPreviewStatus, buildRootLoaderMetricPayload, coords, mapUrl, phase]);

  useEffect(() => {
    if (phase !== 'running' || !mapLoaded || sawMapLoadedRef.current) {
      return;
    }

    sawMapLoadedRef.current = true;
    trackRootLoaderAction('map_loaded', buildRootLoaderMetricPayload());
  }, [buildRootLoaderMetricPayload, mapLoaded, phase]);

  useEffect(() => {
    if (phase !== 'running' || typeof window === 'undefined') {
      return undefined;
    }

    const handlePageHide = () => {
      if (shownAtRef.current == null || hiddenTrackedRef.current) {
        return;
      }

      trackRootLoaderAction(
        'abandoned',
        buildRootLoaderMetricPayload({
          blockers_seen: getRootLoaderMetricList(sawBlockersRef.current),
          saw_address_preview: sawAddressPreviewRef.current,
          saw_address_unavailable: sawAddressUnavailableRef.current,
          saw_map_loaded: sawMapLoadedRef.current,
        }),
      );
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => window.removeEventListener('pagehide', handlePageHide);
  }, [buildRootLoaderMetricPayload, phase]);

  useEffect(() => {
    if (phase !== 'gone' || shownAtRef.current == null || hiddenTrackedRef.current) {
      return;
    }

    hiddenTrackedRef.current = true;
    trackRootLoaderAction(
      'hidden',
      buildRootLoaderMetricPayload({
        blockers_seen: getRootLoaderMetricList(sawBlockersRef.current),
        saw_address_preview: sawAddressPreviewRef.current,
        saw_address_unavailable: sawAddressUnavailableRef.current,
        saw_map_loaded: sawMapLoadedRef.current,
      }),
    );
    shownAtRef.current = null;
  }, [buildRootLoaderMetricPayload, phase]);

  useIsomorphicLayoutEffect(() => {
    if (phase !== 'fading' || typeof window === 'undefined') return undefined;
    const timeout = window.setTimeout(() => {
      finishFade();
    }, 450);
    return () => window.clearTimeout(timeout);
  }, [finishFade, phase]);

  useEffect(() => {
    if (phase !== 'running') return undefined;
    let intervalId: number | undefined;
    const startId = window.setTimeout(() => {
      startTransition(() => setStep(getNextRootLoaderStage));
      intervalId = window.setInterval(() => {
        startTransition(() => setStep(getNextRootLoaderStage));
      }, 4000);
    }, 1800);
    return () => {
      window.clearTimeout(startId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [phase]);

  const fillDelay = step === 0 ? 0 : 600;
  const fillTransition = `400ms ${rootLoaderEase} ${fillDelay}ms`;
  const dotPulseDelay = fillDelay + 400;
  const showAddressContent = hasAddress && isAddressContentRevealed;

  if (phase === 'gone') return null;

  return (
    <div
      id={ROOT_LOADER_ID}
      data-testid="full-screen-loader"
      data-phase={phase}
      aria-live="polite"
      aria-atomic="true"
      aria-busy={phase === 'running'}
      onTransitionEnd={(event) => {
        if (event.propertyName === 'opacity' && phase === 'fading') {
          finishFade();
        }
      }}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: rootLoaderColors.mutedSurface,
        display: 'grid',
        placeItems: 'center',
        boxSizing: 'border-box',
        zIndex: 99999,
        overflow: 'auto',
        opacity: phase === 'fading' ? 0 : 1,
        transition: phase === 'fading' ? 'opacity 400ms ease-out' : 'none',
        pointerEvents: phase === 'fading' ? 'none' : 'auto',
        fontFamily: rootLoaderFontFamily,
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      <style>{`
        #${ROOT_LOADER_ID}, #${ROOT_LOADER_ID} * {
          font-family: ${rootLoaderFontFamily};
        }
        #${ROOT_LOADER_ID} { color: ${rootLoaderColors.content}; }
        #${ROOT_LOADER_ID} p, #${ROOT_LOADER_ID} h1 { margin: 0; }
        @font-face {
          font-family: "Graphik";
          font-style: normal;
          font-weight: 400;
          src:
            local("Graphik-Regular"),
            local("Graphik"),
            url("https://cdn.opendoor.com/fonts/Graphik-Regular-Web.woff2") format("woff2");
        }
        @font-face {
          font-family: "Graphik";
          font-style: normal;
          font-weight: 500;
          src:
            local("Graphik-Medium"),
            local("Graphik"),
            url("https://cdn.opendoor.com/fonts/Graphik-Medium-Web.woff2") format("woff2");
        }
        @keyframes cake-root-loader-dot-pulse {
          0% { box-shadow: 0 0 0 0 rgba(0, 65, 231, 0); }
          18% { box-shadow: 0 0 4px 6px rgba(0, 65, 231, 0.15); }
          42% { box-shadow: 0 0 4px 2px rgba(0, 65, 231, 0.15); }
          68% { box-shadow: 0 0 4px 6px rgba(0, 65, 231, 0.15); }
          88% { box-shadow: 0 0 4px 3px rgba(0, 65, 231, 0.15); }
          100% { box-shadow: 0 0 0 0 rgba(0, 65, 231, 0); }
        }
        #root-loader-address-skeleton .root-loader-skeleton-piece {
          position: relative;
          background: ${rootLoaderColors.mapPlaceholder};
          box-shadow: 0 0 0 1px rgba(121, 118, 117, 0.26);
        }
        #root-loader-address-skeleton::after {
          content: "";
          position: absolute;
          inset: 0;
          background:
            linear-gradient(
              135deg,
              transparent 0%,
              rgba(206, 194, 192, 0) 25%,
              rgba(206, 194, 192, 0.85) 50%,
              rgba(206, 194, 192, 0) 75%,
              transparent 100%
            );
          background-repeat: no-repeat;
          background-size: 180px 180px;
          filter: blur(12px);
          animation: cake-root-loader-skeleton-shimmer 1800ms ease-in-out infinite;
          pointer-events: none;
          -webkit-mask:
            ${rootLoaderSkeletonMask}
            0 0 / 310px 87px no-repeat;
          mask:
            ${rootLoaderSkeletonMask}
            0 0 / 310px 87px no-repeat;
        }
        @keyframes cake-root-loader-skeleton-shimmer {
          0% { background-position: -220px -120px; }
          100% { background-position: 360px 120px; }
        }
        @keyframes cake-root-loader-content-in {
          0% { opacity: 0; transform: translateY(3px) scale(0.995); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          #${ROOT_LOADER_ID},
          #${ROOT_LOADER_ID} *,
          #${ROOT_LOADER_ID} *::before,
          #${ROOT_LOADER_ID} *::after {
            animation-duration: 1ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 1ms !important;
            scroll-behavior: auto !important;
          }
          #root-loader-address-skeleton::after {
            animation: none !important;
            opacity: 0;
          }
        }
      `}</style>
      <div
        style={{
          position: 'absolute',
          top: 43,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <OpendoorLogo color={rootLoaderColors.brand} />
      </div>
      <div
        style={{
          width: rootLoaderContentWidth,
          paddingLeft: rootLoaderContentVisualInset,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            alignSelf: 'stretch',
            position: 'relative',
            minHeight: 236,
          }}
        >
          <div
            style={{
              position: showAddressContent ? 'absolute' : 'relative',
              inset: 0,
              opacity: showAddressContent ? 0 : 1,
              transform: showAddressContent ? 'scale(0.995)' : 'scale(1)',
              transition: `opacity 300ms ease-out, transform 420ms ${rootLoaderAddressEase}`,
              pointerEvents: 'none',
            }}
          >
            <RootLoaderAddressSkeleton />
            <RootLoaderChecklist
              dotPulseDelay={dotPulseDelay}
              fillTransition={fillTransition}
              step={step}
            />
          </div>
          {showAddressContent ? (
            <div
              style={{
                alignSelf: 'stretch',
                position: 'relative',
                animation: 'cake-root-loader-content-in 380ms ease-out both',
              }}
            >
              <RootLoaderPropertyPreview
                localityLine={localityLine}
                streetLine={streetLine}
                coords={coords}
                mapLoaded={mapLoaded}
                mapUrl={mapUrl}
                onMapLoad={() => setMapLoaded(true)}
              />

              <RootLoaderChecklist
                dotPulseDelay={dotPulseDelay}
                fillTransition={fillTransition}
                step={step}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function emitRouteCommitStuck({
  currentRouteContentCommitKey,
  durationMs,
  entriesCount,
  hasCurrentRouteContentCommitted,
  hydrated,
  hydrationEffectsSettled,
  routeContentCommitKey,
}: {
  currentRouteContentCommitKey: string;
  durationMs: number;
  entriesCount: number;
  hasCurrentRouteContentCommitted: boolean;
  hydrated: boolean;
  hydrationEffectsSettled: boolean;
  routeContentCommitKey: string | null;
}): void {
  const key = 'root-loader:route-content-commit';
  if (!IS_PROD) {
    // eslint-disable-next-line no-console -- dev/staging diagnostic for root-loader commit gate stalls
    console.warn('[loader] stuck', key, `${durationMs.toFixed(0)}ms`, {
      currentRouteContentCommitKey,
      entriesCount,
      hasCurrentRouteContentCommitted,
      hydrated,
      hydrationEffectsSettled,
      routeContentCommitKey,
    });
  }
  globalTrackEvent('loader.stuck', 'root-loader', {
    data: {
      key,
      duration_ms: durationMs,
      current_route_content_commit_key: currentRouteContentCommitKey,
      entries_count: entriesCount,
      hydrated,
      hydration_effects_settled: hydrationEffectsSettled,
      route_content_committed: hasCurrentRouteContentCommitted,
      route_content_commit_key: routeContentCommitKey ?? '',
    },
  });
}

/* eslint-enable @opendoor/no-html-elements */
